import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ServiceHelper } from "@core/helpers/service-helper";
import { FinancialInstitute } from "@core/models/financial-insitute.model";
import {
  Intermediary,
  TIntermediariesPaginated,
} from "@core/models/intermediary.model";
import { TPagination } from "@custom-types/sorting";
import { CrudType } from "@enums/crud-type";
import { environment } from "@env/environment";
import {
  AccountManager,
  TAccountManagersPaginated,
} from "@models/account-manager.model";
import { Advisor, TAdvisorsPaginated } from "@models/advisor.model";
import { Country } from "@models/country.model";
import { Document, TDocumentsPaginated } from "@models/document.model";
import { Email, TEmailsPaginated } from "@models/email.model";
import { Etf } from "@models/etf-model";
import { Insurer, TInsurersPaginated } from "@models/insurer.model";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ManagementService {
  constructor(private http: HttpClient) {}

  getAdvisors$(
    searchTerm?: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<Advisor> | TAdvisorsPaginated> {
    const params = ServiceHelper.getListQueryParams(searchTerm, pagination);
    return this.http
      .get<
        Array<Advisor> | TAdvisorsPaginated
      >(`${environment.BASE_URL}advisor`, { params })
      .pipe(
        map(
          (
            response: Array<Advisor> | TAdvisorsPaginated
          ): Array<Advisor> | TAdvisorsPaginated => {
            return response;
          }
        )
      );
  }

  saveAdvisor$(
    advisor: Partial<Advisor>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}advisor/${!create ? `${id}` : ""}`,
      advisor
    );
  }

  saveInsurer$(
    insurer: Partial<Insurer>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}insurer/${!create ? `${id}` : ""}`,
      insurer
    );
  }

  getInsurers$(
    searchTerm?: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<Insurer> | TInsurersPaginated> {
    const params = ServiceHelper.getListQueryParams(searchTerm, pagination);
    return this.http
      .get<
        Array<Insurer> | TInsurersPaginated
      >(`${environment.BASE_URL}insurer`, { params })
      .pipe(
        map(
          (
            response: Array<Insurer> | TInsurersPaginated
          ): Array<Insurer> | TInsurersPaginated => {
            return response;
          }
        )
      );
  }

  getAccountManagers$(
    searchTerm?: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<AccountManager> | TAccountManagersPaginated> {
    const params = ServiceHelper.getListQueryParams(searchTerm, pagination);
    return this.http
      .get<
        Array<AccountManager> | TAccountManagersPaginated
      >(`${environment.BASE_URL}accountmanager`, { params })
      .pipe(
        map(
          (
            response: Array<AccountManager> | TAccountManagersPaginated
          ): Array<AccountManager> | TAccountManagersPaginated => {
            return response;
          }
        )
      );
  }

  getFinancialInstitutions$(
    searchTerm?: string
  ): Observable<Array<FinancialInstitute>> {
    const params = ServiceHelper.getListQueryParams(searchTerm);

    return this.http
      .get<
        Array<FinancialInstitute>
      >(`${environment.BASE_URL}financialinstitute/?deleted=false/`, { params })
      .pipe(
        map(
          (response: Array<FinancialInstitute>): Array<FinancialInstitute> => {
            return response;
          }
        )
      );
  }

  getIntermediaries$(
    searchTerm?: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<Intermediary> | TIntermediariesPaginated> {
    const params = ServiceHelper.getListQueryParams(searchTerm, pagination);
    return this.http
      .get<
        Array<Intermediary> | TIntermediariesPaginated
      >(`${environment.BASE_URL}intermediary`, { params })
      .pipe(
        map(
          (
            response: Array<Intermediary> | TIntermediariesPaginated
          ): Array<Intermediary> | TIntermediariesPaginated => {
            return response;
          }
        )
      );
  }

  getEmails$(
    pagination?: Partial<TPagination>
  ): Observable<Array<Email> | TEmailsPaginated> {
    const params = ServiceHelper.getListQueryParams(undefined, pagination);
    return this.http
      .get<Array<Email>>(`${environment.BASE_URL}emailtemplates/`, { params })
      .pipe(
        map(
          (
            response: Array<Email> | TEmailsPaginated
          ): Array<Email> | TEmailsPaginated => {
            return response;
          }
        )
      );
  }

  getDocuments$(
    pagination?: Partial<TPagination>
  ): Observable<Array<Document> | TDocumentsPaginated> {
    const params = ServiceHelper.getListQueryParams(undefined, pagination);
    return this.http
      .get<Array<Document>>(`${environment.BASE_URL}wordtemplates/`, { params })
      .pipe(
        map(
          (
            response: Array<Document> | TDocumentsPaginated
          ): Array<Document> | TDocumentsPaginated => {
            return response;
          }
        )
      );
  }

  getCountries$(): Observable<Array<Country>> {
    return this.http.get<Array<Country>>(`${environment.BASE_URL}country`).pipe(
      map((response: Array<Country>): Array<Country> => {
        return response;
      })
    );
  }

  saveFinancialInstitution$(
    institution: Partial<FinancialInstitute>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}financialinstitute/${!create ? `${id}` : ""}`,
      institution
    );
  }

  saveAccountManager$(
    accountManager: Partial<AccountManager>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}accountmanager/${!create ? `${id}` : ""}`,
      accountManager
    );
  }

  saveIntermediary$(
    intermediary: Partial<Intermediary>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}intermediary/${!create ? `${id}` : ""}`,
      intermediary
    );
  }

  getDocument$(id: string): Observable<Blob> {
    return this.http.get(`${environment.BASE_URL}wordtemplates/${id}`, {
      responseType: "blob",
    });
  }

  uploadDocument$(id: string, document: File): Observable<unknown> {
    const formData = new FormData();
    formData.append("file", document, document.name);
    return this.http.patch(
      `${environment.BASE_URL}wordtemplates/${id}`,
      formData
    );
  }

  getEmail$(id: string): Observable<Email> {
    return this.http.get<Email>(`${environment.BASE_URL}emailtemplates/${id}`);
  }

  saveEmail$(email: Partial<Email>): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL}emailtemplates/`, email);
  }

  getETFList$(searchTerm?: string): Observable<Array<Etf>> {
    const params = ServiceHelper.getListQueryParams(searchTerm);
    return this.http
      .get<Array<Etf>>(`${environment.BASE_URL}fund/all`, { params })
      .pipe(
        map((response: Array<Etf>): Array<Etf> => {
          return response;
        })
      );
  }

  saveETF$(etf: Partial<Etf>): Observable<unknown> {
    return this.http.post(`${environment.BASE_URL}fund`, [etf]);
  }

  deleteETF$(id: string): Observable<unknown> {
    return this.http.delete(`${environment.BASE_URL}fund/${id}`);
  }

  checkFundIsin$(isin: string): Observable<boolean> {
    let params = new HttpParams({});
    if (isin) {
      params = params.append("isin", isin);
    }
    return this.http
      .get<boolean>(`${environment.BASE_URL}fund/isin`, { params })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }

  checkForExistingEmail$(email: string): Observable<boolean> {
    let params = new HttpParams({});
    params = params.append("email", email);
    return this.http
      .get<boolean>(`${environment.BASE_URL}gfhaccount/email`, { params })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }

  checkForExistingUsername$(username: string): Observable<boolean> {
    let params = new HttpParams({});
    params = params.append("userName", username);
    return this.http
      .get<boolean>(`${environment.BASE_URL}gfhaccount/username`, { params })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }

  validateIban$(iban: string): Observable<Array<FinancialInstitute>> {
    const params = new HttpParams({}).set("iban", iban);
    return this.http.get<Array<FinancialInstitute>>(
      `${environment.BASE_URL}financialinstitute/validateIban`,
      {
        params,
      }
    );
  }
}
